import { useState } from "react";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

const situationOptions = [
  "Litigation & Media Exposure",
  "Regulatory / Banking Pressure",
  "High-Value Dispute",
  "Other High-Stakes Situation",
] as const;

const urgencyOptions = [
  "Active / in the next 48 hours",
  "Upcoming / within 2 weeks",
  "Planning ahead / no immediate deadline",
] as const;

const contactSchema = z.object({
  name: z.string().trim().min(1, "Full name is required").max(100),
  email: z.string().trim().email("Enter a valid email address").max(255),
  organisation: z.string().trim().max(150).optional(),
  role: z.string().trim().max(100).optional(),
  situation_type: z.enum(situationOptions, { required_error: "Select a situation type" }),
  urgency: z.enum(urgencyOptions, { required_error: "Select urgency" }),
  message: z.string().trim().min(1, "Describe your situation").max(2000),
});

type ContactFormProps = {
  source: "homepage" | "contact_page";
  variant?: "home" | "contact";
};

type ContactSubmission = {
  name: string;
  email: string;
  organisation: string | null;
  situation_type: string;
  urgency: string;
  message: string;
  source: "homepage" | "contact_page";
};

const typedSupabase = supabase as unknown as {
  from: (table: "contacts") => {
    insert: (values: ContactSubmission[]) => Promise<{ error: { message: string; code?: string } | null }>;
  };
};

const inputClass = "h-11 rounded-[6px] border-form-border bg-surface text-foreground placeholder:text-body focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0";
const labelClass = "text-[14px] font-semibold text-foreground";

export const ContactForm = ({ source, variant = "home" }: ContactFormProps) => {
  const isContactPage = variant === "contact";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organisation: "",
    role: "",
    situation_type: "",
    urgency: "",
    message: "",
    website: "", // honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData((current) => ({ ...current, [field]: value }));
    if (status) setStatus(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus(null);

    // Honeypot — pretend success, skip insert
    if (formData.website) {
      setStatus({ type: "success", message: isContactPage ? "Received. Expect to hear from us within 24 hours." : "Received. We will be in touch shortly." });
      setFormData({ name: "", email: "", organisation: "", role: "", situation_type: "", urgency: "", message: "", website: "" });
      return;
    }

    const parsed = contactSchema.safeParse({
      ...formData,
      organisation: formData.organisation || undefined,
      role: formData.role || undefined,
      urgency: formData.urgency || undefined,
    });

    if (!parsed.success) {
      setStatus({ type: "error", message: parsed.error.errors[0]?.message || "Please check the form and try again." });
      return;
    }

    setIsSubmitting(true);

    const message = [
      parsed.data.role ? `Role: ${parsed.data.role}` : null,
      parsed.data.message,
    ]
      .filter(Boolean)
      .join("\n\n");

    const { error } = await typedSupabase.from("contacts").insert([
      {
        name: parsed.data.name,
        email: parsed.data.email,
        organisation: parsed.data.organisation || null,
        situation_type: parsed.data.situation_type,
        urgency: parsed.data.urgency,
        message,
        source,
      },
    ]);

    setIsSubmitting(false);

    if (error) {
      if (error.code === "23505") {
        setStatus({ type: "success", message: "We've already received a submission from this email. We'll be in touch shortly." });
        return;
      }
      setStatus({ type: "error", message: "Something went wrong. Please try again or email us directly." });
      return;
    }

    setStatus({
      type: "success",
      message: isContactPage ? "Received. Expect to hear from us within 24 hours." : "Received. We will be in touch shortly.",
    });
    setFormData({ name: "", email: "", organisation: "", role: "", situation_type: "", urgency: "", message: "", website: "" });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" style={{ position: "absolute" }}>
        <label htmlFor={`${source}-website`}>Website</label>
        <input
          id={`${source}-website`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website}
          onChange={(e) => updateField("website", e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${source}-name`} className={labelClass}>Full Name</Label>
        <Input id={`${source}-name`} value={formData.name} onChange={(e) => updateField("name", e.target.value)} required className={inputClass} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${source}-email`} className={labelClass}>Email Address</Label>
        <Input id={`${source}-email`} type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} required className={inputClass} />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${source}-organisation`} className={labelClass}>Organisation</Label>
        <Input id={`${source}-organisation`} value={formData.organisation} onChange={(e) => updateField("organisation", e.target.value)} className={inputClass} />
      </div>

      {isContactPage && (
        <div className="space-y-2">
          <Label htmlFor={`${source}-role`} className={labelClass}>Your Role</Label>
          <Input id={`${source}-role`} placeholder="e.g. General Counsel, CEO, Communications Director" value={formData.role} onChange={(e) => updateField("role", e.target.value)} className={inputClass} />
        </div>
      )}

      <div className="space-y-2">
        <Label className={labelClass}>Situation Type</Label>
        <Select value={formData.situation_type} onValueChange={(value) => updateField("situation_type", value)} required>
          <SelectTrigger className={`${inputClass} text-left text-body`}>
            <SelectValue placeholder="Select a situation" />
          </SelectTrigger>
          <SelectContent className="border-form-border bg-surface text-foreground">
            {situationOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className={labelClass}>How urgent is this?</Label>
        <Select value={formData.urgency} onValueChange={(value) => updateField("urgency", value)} required>
          <SelectTrigger className={`${inputClass} text-left text-body`}>
            <SelectValue placeholder="Select urgency" />
          </SelectTrigger>
          <SelectContent className="border-form-border bg-surface text-foreground">
            {urgencyOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${source}-message`} className={labelClass}>Describe your situation</Label>
        <Textarea
          id={`${source}-message`}
          rows={isContactPage ? 6 : 4}
          placeholder={isContactPage ? "What are you facing? The more context you give, the faster we can assess whether and how we can help." : "Brief context: what are you facing and when?"}
          value={formData.message}
          onChange={(e) => updateField("message", e.target.value)}
          required
          className="rounded-[6px] border-form-border bg-surface text-foreground placeholder:text-body focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0"
        />
      </div>

      {status && (
        <div className={`rounded-[6px] border px-4 py-3 text-sm ${status.type === "success" ? "border-success/40 bg-success/10 text-success" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>
          {status.message}
        </div>
      )}

      <Button type="submit" disabled={isSubmitting} className="h-11 w-full rounded-[6px] bg-amber font-bold text-amber-foreground hover:bg-amber/90 disabled:opacity-60">
        {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {isSubmitting ? "Submitting..." : isContactPage ? "Send Confidentially" : "Submit Confidentially"}
      </Button>

      <p className="text-center text-xs uppercase tracking-[0.18em] text-body">Your information is never shared. Submissions are encrypted and confidential.</p>
    </form>
  );
};
